import { useCallback, useState } from "react";

const STORAGE_PREFIX = "coronis:pinned-doc:";

/** Read the pinned document **id** for a scope. Id only — never the filename. */
const readPinnedId = (scopeKey?: string): string | null => {
  if (!scopeKey) return null;
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + scopeKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { documentId?: string };
    return parsed?.documentId ?? null;
  } catch {
    return null; // corrupt or unavailable storage — treat as no pin
  }
};

/**
 * A user's "pinned" (default) document for a record, persisted to localStorage
 * so the choice survives a reload.
 *
 * Stores the **document id only** — never the filename. Attachment filenames
 * routinely carry member identity (PHI); writing one to unencrypted, same-origin
 * browser storage that survives logout and partner switch is a disclosure
 * channel (ADR-086). Callers resolve the display label from the document at
 * render, where it is transient UI, not persisted state.
 *
 * Rehydrates **synchronously** and re-derives when the scope changes. The record
 * id (hence the scope key) is undefined on the first render, before
 * `useRecordContext` resolves; adjusting state during render is the supported
 * pattern for deriving state from a changing input. Doing this synchronously —
 * rather than in an effect — means the pinned id is correct on the very first
 * render, so a child component's auto-pin effect (which React flushes before a
 * parent's) can never observe a null pin and overwrite a stored one from a warm
 * cache (ADR-087 D9). Persistence is per-browser; cross-device would need a
 * backend field. Pass a stable `scopeKey` (e.g. `policy:{id}`); omit to disable.
 */
export const usePinnedDocument = (scopeKey?: string) => {
  const [state, setState] = useState<{ scope?: string; id: string | null }>(() => ({
    scope: scopeKey,
    id: readPinnedId(scopeKey),
  }));

  // Re-derive synchronously when the scope changes (e.g. the record id resolves
  // after the first render). React discards the in-progress render and re-runs
  // immediately, before committing — no effect, no child/parent ordering race.
  if (state.scope !== scopeKey) {
    setState({ scope: scopeKey, id: readPinnedId(scopeKey) });
  }

  const setPinnedId = useCallback(
    (id: string | null) => {
      setState({ scope: scopeKey, id });
      if (!scopeKey) return;
      try {
        if (id) localStorage.setItem(STORAGE_PREFIX + scopeKey, JSON.stringify({ documentId: id }));
        else localStorage.removeItem(STORAGE_PREFIX + scopeKey);
      } catch {
        /* storage unavailable (private mode, quota) — pin stays in-memory */
      }
    },
    [scopeKey]
  );

  return { pinnedId: state.id, setPinnedId };
};
